import React, { useCallback, useEffect, useMemo, useReducer } from 'react'
import { Main, MainBody, MainTop, MainTopTitle } from '@/components/Layout'
import context from '@/context'
import { submitReportProgress } from '@/enums/submitReportProgress'
import Resx from '@/components/Resx'
import { openErrorDialog } from '@/utils/dialogHelpers'
import LoadingIndicator from '@/components/LoadingIndicator'
import { StartSubmissionCard } from './startSubmissionCard'
import { RecalculatingSubmissionCard } from './recalculatingSubmissionCard'
import { ValidateSubmissionCard } from './validateSubmissionCard'
import { SignSubmissionCard } from './signSubmissionCard'
import { CreateTicketCard } from './createTicketCard'
import { AllDoneCard } from './allDoneCard'
import { useSignalR } from '@/hooks/useSignalR'
import { GenerateXmlCard } from '@/report-app/pages/reporting/submissions/submit/generateXmlCard'
import { Card } from '@/components/cards/card'
import { getStore } from '@/store'
import { push } from 'react-router-redux'

const initialState = {
  loaded: false,
  backgroundJobMetadata: [],
  submitProgress: null
}

const actionTypes = {
  setLoadedState: 'setInitialState',
  setBackgroundJobStatus: 'setBackgroundJobStatus',
  setSubmitProgress: 'setSubmitProgress',
  interruptSigning: 'interruptSigning',
  submitPossible: false,
  isForecasting: false,
  hasRunningBgJobs: false
}

function reducer (state = initialState, action) {
  switch (action.type) {
    case actionTypes.setLoadedState:
      const dataTransformed = action.backgroundJobMetadata.map(x => {
        return { ...x, data: JSON.parse(x.data)
        }
      }).filter(x => (x.data || {}).submitProgress)

      return { ...state,
        submitProgress: action.submitProgress,
        backgroundJobMetadata: dataTransformed,
        loaded: true,
        submitPossible: action.submitPossible,
        isForecasting: action.isForecasting,
        hasRunningBgJobs: !!dataTransformed.find(x => x.status === 0)
      }

    case actionTypes.setBackgroundJobStatus:
      if (!state.loaded) return { ...state }
      const newArray = state.backgroundJobMetadata
        .filter(x => (x.data || {}).submitProgress !== action.jobProgress.data.submitProgress)

      let newSubmitProgress = state.submitProgress
      const hasRunningBgJobs = action.jobProgress.status === 0

      if (action.jobProgress.status !== 2 && action.submitProgress !== null) {
        newSubmitProgress = action.submitProgress
      }

      return { ...state,
        backgroundJobMetadata: [ ...newArray, action.jobProgress ],
        submitProgress: newSubmitProgress,
        hasRunningBgJobs
      }

    case actionTypes.setSubmitProgress:
      return { ...state, submitProgress: action.submitProgress }

    case actionTypes.interruptSigning:
      return { ...state,
        submitProgress: submitReportProgress.enum.NotStarted,
        backgroundJobMetadata: [],
        hasRunningBgJobs: false
      }
  }
}

function SubmitReport (props) {
  const isJobSuccessful = (status) => status === 1 || status === 3 || status === 4

  const reportInstanceGroupId = props.match.params.parentId
  const [state, dispatch] = useReducer(reducer, initialState)
  const { submitProgress, backgroundJobMetadata, submitPossible, isForecasting, hasRunningBgJobs } = state

  useSignalR('jobProgress', async jobProgress => {
    // For some reason refreshing signalR session causes stop to conversion of data from json so I do it manually
    const dataFixed = (jobProgress.data && typeof jobProgress.data === 'string') ? JSON.parse(jobProgress.data) : jobProgress.data
    const jobProgressFixed = { ...jobProgress, data: dataFixed }

    if (jobProgressFixed.data &&
      (jobProgressFixed.data.reportInstanceGroupId || {}).toString() === reportInstanceGroupId &&
      jobProgressFixed.data.submitProgress) {
      if (jobProgressFixed.data.submitProgress === submitReportProgress.enum.Recalculated && isJobSuccessful(jobProgressFixed.status)) {
        dispatch({ type: actionTypes.setBackgroundJobStatus, jobProgress: jobProgressFixed, submitProgress: submitReportProgress.enum.Recalculated })
      } else if (jobProgressFixed.data.submitProgress === submitReportProgress.enum.Validated && isJobSuccessful(jobProgressFixed.status)) {
        dispatch({ type: actionTypes.setBackgroundJobStatus, jobProgress: jobProgressFixed, submitProgress: submitReportProgress.enum.Validated })
      } else if (jobProgressFixed.data.submitProgress === submitReportProgress.enum.XmlGenerated && isJobSuccessful(jobProgressFixed.status)) {
        dispatch({ type: actionTypes.setBackgroundJobStatus, jobProgress: jobProgressFixed, submitProgress: submitReportProgress.enum.XmlGenerated })
      } else {
        dispatch({ type: actionTypes.setBackgroundJobStatus, jobProgress: jobProgressFixed, submitProgress: null })
      }
    }
  }, [backgroundJobMetadata.length])

  const onJobStarted = useCallback((targetProgress) => {
    dispatch({ type: actionTypes.setBackgroundJobStatus,
      jobProgress: {
        data: { submitProgress: targetProgress },
        status: 0
      },
      submitProgress: null
    })
  }, [])

  useEffect(() => {
    context.api.reportSubmissionGroupService.getSubmitProgress(reportInstanceGroupId)
      .then(r => dispatch({
        type: actionTypes.setLoadedState,
        backgroundJobMetadata: r.backgroundJobMetadata,
        submitProgress: r.submitReportProgress,
        submitPossible: r.submitPossible,
        isForecasting: r.isForecasting
      }))
      .catch(openErrorDialog)
  }, [reportInstanceGroupId])

  const backUrl = useMemo(() => isForecasting
    ? `/forecast-report-submission-groups/${reportInstanceGroupId}/report-submissions`
    : `/report-submission-groups/${reportInstanceGroupId}/report-submissions`, [isForecasting, reportInstanceGroupId])

  useEffect(() => {
    if (submitProgress === submitReportProgress.enum.TicketCreated) {
      getStore().dispatch(push(backUrl))
    }
  }, [backUrl, submitProgress])

  const onSubmitProgressChange = useCallback(x => dispatch({
    type: actionTypes.setSubmitProgress,
    submitProgress: x
  }), [])

  const stopSubmission = useCallback(() =>
    context.api.reportSubmissionGroupService.interruptSigning(reportInstanceGroupId)
      .then(() => dispatch({ type: actionTypes.interruptSigning }))
      .catch(openErrorDialog), [reportInstanceGroupId])

  return <Main>
    <MainTop>
      <MainTopTitle backUrl={backUrl}>
        <Resx name='Reporting.SubmitReport' />
      </MainTopTitle>
    </MainTop>
    <MainBody>
      {submitProgress === null && <LoadingIndicator />}
      {submitProgress !== null && !submitPossible && <Card icon='bx-error' headerText='Reporting.SubmissionError' borderColor='danger'>
        <Resx name='Reporting.InvalidStateForSubmit' />
      </Card>}
      {submitProgress !== null && submitPossible && <React.Fragment>
        <StartSubmissionCard reportInstanceGroupId={reportInstanceGroupId}
          submitProgress={submitProgress}
          onSubmitProgressChange={onSubmitProgressChange}
          onStopSubmission={stopSubmission}
          hasRunningBgJobs={hasRunningBgJobs} />
        <RecalculatingSubmissionCard reportInstanceGroupId={reportInstanceGroupId}
          submitProgress={submitProgress}
          backgroundJobMetadata={backgroundJobMetadata}
          onJobStarted={onJobStarted} />
        <ValidateSubmissionCard reportInstanceGroupId={reportInstanceGroupId}
          submitProgress={submitProgress}
          backgroundJobMetadata={backgroundJobMetadata}
          onJobStarted={onJobStarted} />
        <GenerateXmlCard reportInstanceGroupId={reportInstanceGroupId}
          submitProgress={submitProgress}
          backgroundJobMetadata={backgroundJobMetadata}
          onJobStarted={onJobStarted} />
        <SignSubmissionCard
          submitProgress={submitProgress}
          reportInstanceGroupId={reportInstanceGroupId}
          onSubmitProgressChange={onSubmitProgressChange} />
        <CreateTicketCard submitProgress={submitProgress}
          reportInstanceGroupId={reportInstanceGroupId}
          onSubmitProgressChange={onSubmitProgressChange} />
        <AllDoneCard submitProgress={submitProgress} />
      </React.Fragment>}
    </MainBody>
  </Main>
}

export { SubmitReport }



// WEBPACK FOOTER //
// ./src/report-app/pages/reporting/submissions/submit/submitReport.js